import { createReducer, on } from "@ngrx/store";

import { MetricsActions } from "./metrics.actions";
import {
  AggregatedMetricsResponse,
  MetricsResponse,
  TimeSeriesMetric,
} from "./metrics.model";

export const metricsFeatureKey = "metrics";

export interface MetricsState {
  metrics: AggregatedMetricsResponse | null;
  timeSeriesData: TimeSeriesMetric[];
  error: string | null;
  updatingError: boolean;
  selectedProducts: string[];
  selectedRequestTypes: string[];
}

export const initialMetricsState: MetricsState = {
  metrics: null,
  timeSeriesData: [],
  error: null,
  updatingError: false,
  selectedProducts: [],
  selectedRequestTypes: [],
};

export const metricsReducer = createReducer(
  initialMetricsState,
  on(MetricsActions.loadMetrics, state => ({
    ...state,
    error: state.updatingError ? null : state.error,
  })),
  on(MetricsActions.loadMetricsRawData, (state, { regionMetrics }) => {
    const processedRegions = regionMetrics.map(regionData => {
      const aggregatedMetrics = aggregateMetricsArray(regionData.metrics);
      const timeSeriesData = generateTimeSeriesData(
        regionData.metrics,
        regionData.region
      );

      return {
        region: regionData.region,
        metrics: aggregatedMetrics,
        timeSeriesData,
      };
    });

    const aggregatedMetrics = aggregateRegionResults(
      processedRegions.map(r => r.metrics)
    );

    const allTimeSeriesData = processedRegions.flatMap(
      result => result.timeSeriesData
    );

    return {
      ...state,
      metrics: aggregatedMetrics,
      timeSeriesData: allTimeSeriesData,
    };
  }),
  on(
    MetricsActions.loadMetricsSuccess,
    (state, { metrics, timeSeriesData }) => ({
      ...state,
      metrics,
      timeSeriesData,
    })
  ),
  on(MetricsActions.setErrorState, (state, { error }) => ({
    ...state,
    error,
    updatingError: false,
  })),
  on(MetricsActions.setUpdatingErrorFlag, (state, { updating }) => ({
    ...state,
    updatingError: updating,
  })),
  on(MetricsActions.toggleSelectedProduct, (state, { product }) => {
    const isSelected = state.selectedProducts.includes(product);
    const selectedProducts = isSelected
      ? state.selectedProducts.filter(p => p !== product)
      : [...state.selectedProducts, product];

    return {
      ...state,
      selectedProducts,
    };
  }),
  on(MetricsActions.clearSelectedProducts, state => ({
    ...state,
    selectedProducts: [],
  })),
  on(MetricsActions.selectAllProducts, state => {
    // The component will handle setting all products in the filter
    // and then trigger a metrics load with the updated selection
    // We'll just return the current state here with an empty array
    // to ensure the component's subscription is triggered
    return {
      ...state,
      selectedProducts: [],
    };
  }),
  on(MetricsActions.toggleSelectedRequestType, (state, { requestType }) => {
    const isSelected = state.selectedRequestTypes.includes(requestType);
    const selectedRequestTypes = isSelected
      ? state.selectedRequestTypes.filter(rt => rt !== requestType)
      : [...state.selectedRequestTypes, requestType];

    return {
      ...state,
      selectedRequestTypes,
    };
  }),
  on(MetricsActions.clearSelectedRequestTypes, state => ({
    ...state,
    selectedRequestTypes: [],
  })),
  on(MetricsActions.selectAllRequestTypes, state => {
    // The component will handle setting all request types in the filter
    // and then trigger a metrics load with the updated selection
    // We'll just return the current state here with an empty array
    // to ensure the component's subscription is triggered
    return {
      ...state,
      selectedRequestTypes: [],
    };
  }),
  on(MetricsActions.setSelectedProducts, (state, { products }) => ({
    ...state,
    selectedProducts: products,
  })),
  on(MetricsActions.setSelectedRequestTypes, (state, { requestTypes }) => ({
    ...state,
    selectedRequestTypes: requestTypes,
  }))
);

export function aggregateMetricsArray(
  metricsArray: MetricsResponse[]
): AggregatedMetricsResponse {
  return metricsArray.reduce(
    (acc, curr) => {
      return {
        totalRequestCount:
          (acc.totalRequestCount || 0) + (curr.totalRequestCount || 0),
        totalPageCount: (acc.totalPageCount || 0) + (curr.totalPageCount || 0),
        totalSuccessfulRequestCount:
          (acc.totalSuccessfulRequestCount || 0) +
          (curr.totalSuccessfulRequestCount || 0),
        totalRejectedRequestCount:
          (acc.totalRejectedRequestCount || 0) +
          (curr.totalRejectedRequestCount || 0),
        totalFailedRequestCount:
          (acc.totalFailedRequestCount || 0) +
          (curr.totalFailedRequestCount || 0),
        totalFileSizeBytes:
          (acc.totalFileSizeBytes || 0) + (curr.totalFileSizeBytes || 0),
      };
    },
    {
      totalRequestCount: 0,
      totalPageCount: 0,
      totalSuccessfulRequestCount: 0,
      totalRejectedRequestCount: 0,
      totalFailedRequestCount: 0,
      totalFileSizeBytes: 0,
    }
  );
}

export function aggregateRegionResults(
  regionResults: AggregatedMetricsResponse[]
): AggregatedMetricsResponse {
  return regionResults.reduce(
    (acc, curr) => {
      return {
        totalRequestCount: acc.totalRequestCount + curr.totalRequestCount,
        totalPageCount: acc.totalPageCount + curr.totalPageCount,
        totalSuccessfulRequestCount:
          acc.totalSuccessfulRequestCount + curr.totalSuccessfulRequestCount,
        totalRejectedRequestCount:
          acc.totalRejectedRequestCount + curr.totalRejectedRequestCount,
        totalFailedRequestCount:
          acc.totalFailedRequestCount + curr.totalFailedRequestCount,
        totalFileSizeBytes: acc.totalFileSizeBytes + curr.totalFileSizeBytes,
      };
    },
    {
      totalRequestCount: 0,
      totalPageCount: 0,
      totalSuccessfulRequestCount: 0,
      totalRejectedRequestCount: 0,
      totalFailedRequestCount: 0,
      totalFileSizeBytes: 0,
    }
  );
}

export function generateTimeSeriesData(
  metricsArray: MetricsResponse[],
  region?: string
): TimeSeriesMetric[] {
  return metricsArray.map(metric => ({
    completedOn: metric.completedOn,
    requestCount: metric.totalRequestCount || 0,
    successfulRequestCount: metric.totalSuccessfulRequestCount || 0,
    failedRequestCount: metric.totalFailedRequestCount || 0,
    rejectedRequestCount: metric.totalRejectedRequestCount || 0,
    pageCount: metric.totalPageCount || 0,
    fileSizeBytes: metric.totalFileSizeBytes || 0,
    region: region,
    requestType: metric.requestType,
    productId: metric.productId,
  }));
}
